import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import LoadingSpinner from "./LoadingSpinner";
import { ROUTES } from "../../utils/constants";
import firestoreService from "../../services/firestoreService";

/**
 * RequirePasswordChange Component
 *
 * Enforces password change for users with temporary passwords.
 * Redirects to change password page if passwordChanged is false.
 *
 * Requirements: 7.2
 */
const RequirePasswordChange = ({ children }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkPasswordChangeRequired = async () => {
      if (!isAuthenticated || !user) {
        setLoading(false);
        return;
      }

      try {
        // Check if user has a user_account document with passwordChanged = false
        const userAccount = await firestoreService.getUserAccount(user.email);

        if (userAccount && userAccount.passwordChanged === false) {
          setPasswordChangeRequired(true);
        } else {
          setPasswordChangeRequired(false);
        }
      } catch (error) {
        console.error("Error checking password change requirement:", error);
        // If we can't check, assume no password change required
        setPasswordChangeRequired(false);
      } finally {
        setLoading(false);
      }
    };

    checkPasswordChangeRequired();
  }, [isAuthenticated, user]);

  // Show loading spinner while checking
  if (authLoading || loading) {
    return (
      <div className="password-check-loading">
        <LoadingSpinner />
        <p>Checking account status...</p>
      </div>
    );
  }

  // If not authenticated, let ProtectedRoute handle it
  if (!isAuthenticated) {
    return children;
  }

  // If password change is required, redirect to change password page
  if (passwordChangeRequired) {
    return <Navigate to={ROUTES.CHANGE_PASSWORD} replace />;
  }

  // Render the protected component
  return children;
};

export default RequirePasswordChange;
